import {
  ChangeDetectionStrategy,
  Component,
  type ComponentRef,
  Injector,
  PendingTasks,
  ViewContainerRef,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { RouterOutlet } from '@angular/router';
import {
  MARCA_DE_RESPALDO,
  MarcasDeSoporteMontadas,
} from '@/app/shared/impersonation-banner/marcas-montadas';
import { ImpersonationService } from '../services/impersonation.service';
import { ThemeService } from '../services/theme.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet],
  templateUrl: './app.html',
  styleUrl: './app.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App {
  protected readonly title = signal('agents-booster-front');
  private themeService = inject(ThemeService);

  private readonly marcas = inject(MarcasDeSoporteMontadas);
  private readonly haySesionDeSoporte = inject(ImpersonationService).isActive;
  private readonly injector = inject(Injector);
  private readonly pendientes = inject(PendingTasks);
  private readonly lugar = viewChild.required('marcaDeRespaldo', { read: ViewContainerRef });
  private marca: ComponentRef<unknown> | null = null;
  private cargando = false;

  /**
   * La marca del modo soporte de RESPALDO (Historia 9.1, 4.º criterio): se dibuja cuando hay una
   * sesión de soporte y no se ve la de ningún layout —el Design Kit, una URL inexistente que cae
   * en `/sign-in`, cualquier pantalla que cuelgue de la raíz, o la pantalla de «cierre sin
   * confirmar» del shell, que tapa su propia ranura—. Ver `marcas-montadas.ts`.
   *
   * **Se importa a mano, y sólo si hace falta.** La raíz está en el arranque de todo el mundo, y
   * la banda con sus dependencias pesaba 11 kB en el bundle inicial (483,6 → 494,9 de un techo
   * de 500). Un `@defer` la sacaba del arranque pero traía su propio runtime (~7 kB): esto no.
   */
  constructor() {
    effect(() => {
      const hayQueDibujarla = this.haySesionDeSoporte() && !this.marcas.hayUna();
      const lugar = this.lugar();
      untracked(() => {
        if (hayQueDibujarla) void this.dibujar(lugar);
        else this.sacar();
      });
    });
  }

  /**
   * La importación es una tarea pendiente de la app (`PendingTasks`): mientras viaja, la app no
   * está estable. Sin eso, `whenStable()` —el de los tests, el del e2e— terminaba antes de que la
   * marca existiera, y una pantalla «estable» en modo soporte se veía sin ella siempre que el
   * módulo de la banda no estuviera ya cargado (`la-marca-de-soporte-en-todas-las-zonas` caía en
   * `/sign-in` con «0 marcas» sólo con la máquina cargada, 2026-10-08).
   */
  private async dibujar(lugar: ViewContainerRef): Promise<void> {
    if (this.marca !== null || this.cargando) return;
    this.cargando = true;
    const terminar = this.pendientes.add();
    try {
      const banda =
        await import('@/app/shared/impersonation-banner/impersonation-banner.component');
      // Mientras viajaba la importación pudo terminar la sesión o montarse un layout.
      if (this.marca !== null || !this.haySesionDeSoporte() || this.marcas.hayUna()) return;
      this.marca = lugar.createComponent(banda.ImpersonationBannerComponent, {
        injector: Injector.create({
          providers: [{ provide: MARCA_DE_RESPALDO, useValue: true }],
          parent: this.injector,
        }),
      });
      // Pegada arriba: una página que scrollea el documento entero no se la lleva.
      (this.marca.location.nativeElement as HTMLElement).classList.add(
        'sticky',
        'top-0',
        'z-50',
        'block',
      );
    } finally {
      this.cargando = false;
      terminar();
    }
  }

  private sacar(): void {
    this.marca?.destroy();
    this.marca = null;
  }
}
